import React, { useState, useEffect, memo } from 'react';
import { get, isNil, isString, size } from 'lodash';
import { FormControlLabel } from '@mui/material';

import { InfoOutlined } from '@mui/icons-material';
import Loader from '../../../../common/components/Loader/Loader';
import { Dialog } from '../../../../common/components/Dialog/Dialog';
import Button from '../../../../common/components/Button/Button';
import TextField from '../../../../common/components/TextField/TextField';
import { sortingLowerCase } from '../../../../common/services/I2P2Helper';
import Checkbox from '../../../../common/components/Checkbox/Checkbox';
import { NoReleaseNotesMessage } from '../../../MultiFwUpdate/components/Toolbar/components/Autocomplete/ReleaseNotesTooltip';
import Autocomplete from '../../../../common/components/Autocomplete/Autocomplete';
import Tooltip from '../../../../common/components/Tooltip';

import './styles.scss';

const loaderContainerStyle = {
  position: 'relative',
  padding: 25,
  margin: '30px 80px',
};

const AutocompleteInputStyle = {
  margin: '0 0 20px',
};

function FirmwareUpdateDialog({
  loadUpdateFiles,
  modalOpen,
  setForceFWUpdate,
  onCancel,
  forceUpdate,
  firmwareUpdateWithFile,
  c_uuid,
  c_online,
  selectedFileId,
  filesList,
  isFetching,
  error,
  selectFile,
}) {
  const [prevModalOpen, setPrevModalOpen] = useState(false);

  useEffect(() => {
    if (prevModalOpen !== modalOpen && modalOpen) {
      loadUpdateFiles();
    }

    setPrevModalOpen(modalOpen);
  }, [modalOpen]);

  const onSubmit = (activeFile) => {
    activeFile.force_update = forceUpdate;
    firmwareUpdateWithFile(activeFile, c_uuid, c_online);
  };

  const handleForceUpdateChange = (event, value) => {
    setForceFWUpdate(value);
  };

  const processFileDescription = (activeFile) => {
    let description = get(activeFile, 'packages');

    if (isString(description)) {
      description = description
        .split(';')
        .map((item) => item.trim())
        .join(';\n');
      if (description.endsWith(';\n')) {
        description = description.substring(0, description.length - 1);
      }
    }

    return description || '';
  };

  const activeFile = filesList.find((file) => file.uuid === selectedFileId);
  const description = processFileDescription(activeFile);
  const releaseNotes = get(activeFile, 'releaseNotes');

  const actions = (
    <div className="fwUpdateDialog__actions">
      <div>
        <div className="fwUpdateDialog__tooltip">
          <FormControlLabel control={<Checkbox checked={forceUpdate} onChange={handleForceUpdateChange} />} label="Force Update" />
          <Tooltip title="FW Update might take a couple of minutes. During this process charger will be displayed as Offline and will change its status to online after the update is completed. As a reminder, you can always see latest FW Version under the Details tab.">
            <InfoOutlined fontSize="small" style={{ color: '#2196F3' }} />
          </Tooltip>
        </div>
        {forceUpdate && (
          <div className="fwUpdateDialog__forceUpdate">
            FW Update will be sent to the device in spite of any restrictive validation rules only if the device is not in factory boot
            mode.
          </div>
        )}
      </div>
      <div className="fwUpdateDialog__modalButtons">
        <Button disabled={isFetching} onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={isNil(activeFile) || isFetching} onClick={() => onSubmit(activeFile)}>
          Update
        </Button>
      </div>
    </div>
  );

  const loader = (
    <div style={loaderContainerStyle}>
      <Loader />
    </div>
  );

  const sortedList = filesList.sort((a, b) => sortingLowerCase(a, b, 'firmwareVersionName'));

  const menu = (
    <>
      <Autocomplete
        key="fw-list"
        options={sortedList}
        getOptionLabel={(option) => option.firmwareVersionName}
        onChange={(event, newValue) => {
          if (newValue) {
            selectFile(newValue.uuid);
          } else {
            selectFile('');
          }
        }}
        renderInput={(params) => (
          <TextField label="FW Update Name" fullWidth style={AutocompleteInputStyle} {...params} InputLabelProps={{ shrink: true }} />
        )}
      />

      <TextField
        fullWidth
        label="Description"
        disabled
        multiline
        value={description}
        minRows={1}
        maxRows={15}
        style={{ color: 'rgba(0, 0, 0, 0.38)!important' }}
      />

      <TextField
        fullWidth
        label="Release Notes"
        disabled
        multiline
        value={releaseNotes || ''}
        placeholder={activeFile ? NoReleaseNotesMessage : ''}
        minRows={1}
        maxRows={15}
        style={{ color: 'rgba(0, 0, 0, 0.38)!important', marginTop: 20 }}
      />

      {size(error) > 0 && <div className="error">{error}</div>}
    </>
  );

  return (
    <Dialog actions={actions} title="FW Update" open={modalOpen} modal onClose={onCancel} fullWidth>
      {isFetching ? loader : menu}
    </Dialog>
  );
}

export default memo(FirmwareUpdateDialog);
